import React, {
  createContext,
  useContext,
  useState,
  useLayoutEffect,
} from 'react';
import { applyMode, Mode } from '@cloudscape-design/global-styles';

const STORAGE_KEY = 'langhub-dark-mode';

interface DarkModeContextValue {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const DarkModeContext = createContext<DarkModeContextValue>({
  isDarkMode: false,
  toggleDarkMode: () => {},
});

export const useDarkMode = (): DarkModeContextValue =>
  useContext(DarkModeContext);

export const DarkModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useLayoutEffect(() => {
    const mode = isDarkMode ? Mode.Dark : Mode.Light;
    applyMode(mode);

    // Set data-theme attribute for non-Cloudscape custom components
    document.documentElement.dataset.theme = isDarkMode ? 'dark' : 'light';

    // The internal @amzn/awsui-components-console fork uses
    // 'awsui-polaris-dark-mode' for its CSS custom property selectors,
    // in addition to the standard 'awsui-dark-mode' class.
    if (isDarkMode) {
      document.body.classList.add('awsui-dark-mode', 'awsui-polaris-dark-mode');
    } else {
      document.body.classList.remove(
        'awsui-dark-mode',
        'awsui-polaris-dark-mode',
      );
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // localStorage may be unavailable in some environments
      }
      return next;
    });
  };

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export default DarkModeContext;
